import './styles.css';
import { FPS_LIMIT, FPS_LIMIT_ECO, GAME_NAME } from './config';
import { DebugOverlay, type OverlayState } from './debug/overlay';
import { installErrorCapture, pushError } from './debug/errors';
import { StateTimeline, lerpInto } from './render/interp';
import { createScene, type SceneHandle } from './render/scene';
import { CTRL, createSabState, prevSlot, sabViews, slotOffset, stateBytes, type FromWorker, type SabViews } from './shared/protocol';
import { wasmSimdSupported } from './ui/platform';
import brandsJson from '../data/brands.json';
import type { Report } from './data/types';
import { httpSource } from './cars/loader';
import { createGarage } from './ui/garage';
import { createLab } from './ui/lab';
import { createDrive } from './ui/drive';
import { requestTiltPermission, setupInstallHint, waitForStart } from './ui/screens';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

installErrorCapture();
$('gameName').textContent = GAME_NAME;
document.title = GAME_NAME;
setupInstallHint($('install'), $<HTMLButtonElement>('installClose'));

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => pushError(`service worker: ${String(e)}`));
  });
}

const overlay = new DebugOverlay($('debug'));
const state: OverlayState = {
  fps: 0,
  stats: null,
  scene: null,
  isolated: self.crossOriginIsolated === true,
  transfer: '—',
  simdModule: false,
  simdBrowser: wasmSimdSupported(),
  nodes: 0,
  beams: 0,
  sabBytes: 0,
  unstableBeam: -1,
  unstableNode: -1,
  eco: false
};

async function boot(): Promise<void> {
  $('startStatus').textContent = state.isolated ? '' : 'Atenție: pagina nu e izolată cross-origin; folosesc postMessage.';
  await waitForStart($<HTMLButtonElement>('startBtn'), $('start'));

  const worker = new Worker(new URL('./worker/physics.worker.ts', import.meta.url), { type: 'module' });
  worker.addEventListener('error', (e) => pushError(`worker: ${e.message}`));

  let scene: SceneHandle | null = null;
  let views: SabViews | null = null;
  let nodeCount = 0;
  let msgPrev: Float32Array | null = null;
  let msgLatest: Float32Array | null = null;
  let msgBroken = new Uint8Array(4);
  let msgSeq = 0;
  const pendingValidations = new Map<number, (r: Report) => void>();
  let validationId = 0;

  const ready = new Promise<Extract<FromWorker, { type: 'ready' }>>((resolve) => {
    worker.addEventListener('message', (e: MessageEvent<FromWorker>) => {
      const m = e.data;
      if (m.type === 'ready') resolve(m);
      else if (m.type === 'stats') state.stats = m.stats;
      else if (m.type === 'error') pushError(m.message);
      else if (m.type === 'validation') pendingValidations.get(m.requestId)?.(JSON.parse(m.report) as Report);
      else if (m.type === 'state') {
        msgPrev = msgLatest;
        msgLatest = new Float32Array(m.positions);
        msgBroken = new Uint8Array(m.broken);
        msgSeq = m.seq;
      }
    });
  });
  worker.postMessage({ type: 'init' });

  // garajul e disponibil imediat; validarea Rust așteaptă ca worker-ul să fi încărcat WASM-ul
  // garajul: validarea Rust rulează în worker, în același WASM ca fizica
  const garage = createGarage($('garage'), {
    models: httpSource('/cars'),
    transcoderPath: '/basis/',
    validateRust: (json) => ready.then(() => new Promise<Report | null>((resolve) => {
      const requestId = ++validationId;
      pendingValidations.set(requestId, (r) => { pendingValidations.delete(requestId); resolve(r); });
      worker.postMessage({ type: 'validate', requestId, json, brands: JSON.stringify(brandsJson) });
      setTimeout(() => { if (pendingValidations.delete(requestId)) resolve(null); }, 5000);
    }))
  });
  $('btnGarage').addEventListener('click', () => garage.open());
  const lab = createLab($('lab'), { models: httpSource('/cars') });
  $('btnLab').addEventListener('click', () => lab.open());
  const drive = createDrive($('drive'), { models: httpSource('/cars') });
  $('btnDrive').addEventListener('click', () => drive.open());

  const r = await ready;
  nodeCount = r.nodeCount;
  state.nodes = r.nodeCount;
  state.beams = r.beamCount;
  state.simdModule = r.simd;
  state.unstableBeam = r.unstableBeam;
  state.unstableNode = r.unstableNode;

  const forceWebGL = new URLSearchParams(location.search).has('webgl');
  scene = await createScene($('view'), r.nodeCount, r.beamPairs, { forceWebGL });
  state.scene = scene.info();

  if (state.isolated && typeof SharedArrayBuffer !== 'undefined') {
    const sab = createSabState(r.nodeCount, r.beamCount);
    views = sabViews(sab);
    state.transfer = 'SharedArrayBuffer';
    state.sabBytes = stateBytes(r.nodeCount, r.beamCount);
    worker.postMessage({ type: 'attach', sab });
  } else {
    state.transfer = 'postMessage';
    worker.postMessage({ type: 'attach', sab: null });
  }



  // butoane
  let beamsOn = true;
  let eco = false;
  $('btnReset').addEventListener('click', () => worker.postMessage({ type: 'reset', height: 1.5, tilt: 0 }));
  $('btnDrop').addEventListener('click', () => worker.postMessage({ type: 'reset', height: 10, tilt: 0.35 }));
  $('btnPush').addEventListener('click', () => worker.postMessage({ type: 'impulse', vx: (Math.random() - 0.5) * 8, vy: 6, vz: (Math.random() - 0.5) * 8 }));
  $('btnBeams').addEventListener('click', (e) => {
    beamsOn = !beamsOn;
    scene?.setBeamsVisible(beamsOn);
    (e.currentTarget as HTMLElement).setAttribute('aria-pressed', String(beamsOn));
  });
  $('btnNodes').addEventListener('click', (e) => {
    const b = e.currentTarget as HTMLElement;
    const on = b.getAttribute('aria-pressed') !== 'true';
    scene?.setNodesVisible(on);
    b.setAttribute('aria-pressed', String(on));
  });
  $('btnEco').addEventListener('click', (e) => {
    eco = !eco;
    state.eco = eco;
    scene?.setEco(eco);
    (e.currentTarget as HTMLElement).setAttribute('aria-pressed', String(eco));
  });
  $('btnDebug').addEventListener('click', (e) => {
    overlay.setVisible(!overlay.visible);
    (e.currentTarget as HTMLElement).setAttribute('aria-pressed', String(overlay.visible));
  });
  $('btnTilt').addEventListener('click', async (e) => {
    const res = await requestTiltPermission();
    (e.currentTarget as HTMLElement).textContent = res === 'granted' ? 'Înclinare: permisă' : res === 'denied' ? 'Înclinare: refuzată' : 'Fără senzor';
  });
  document.addEventListener('visibilitychange', () => worker.postMessage({ type: 'pause', paused: document.hidden }));
  new ResizeObserver(() => scene?.resize()).observe($('view'));

  // bucla de randare, limitată la 60 FPS (30 pe economie)
  const timeline = new StateTimeline();
  const scratch = new Float32Array(nodeCount * 3);
  let lastFrame = 0;
  let fpsFrames = 0;
  let fpsT0 = performance.now();
  const frame = (now: number) => {
    requestAnimationFrame(frame);
    const minDt = 1000 / (eco ? FPS_LIMIT_ECO : FPS_LIMIT);
    if (now - lastFrame < minDt - 2) return;
    lastFrame = now;
    if (!scene) return;
    let broken: Uint8Array;
    if (views) {
      const seq = Atomics.load(views.control, CTRL.SEQ);
      if (seq < 2) return;
      timeline.observe(seq, now);
      const latest = Atomics.load(views.control, CTRL.LATEST);
      const prev = prevSlot(latest);
      lerpInto(scratch, views.positions, slotOffset(prev, nodeCount), views.positions, slotOffset(latest, nodeCount), nodeCount * 3, timeline.alpha(now));
      broken = views.broken;
    } else {
      if (!msgLatest) return;
      timeline.observe(msgSeq, now);
      const a = msgPrev ?? msgLatest;
      lerpInto(scratch, a, 0, msgLatest, 0, nodeCount * 3, timeline.alpha(now));
      broken = msgBroken;
    }
    scene.update(scratch, broken);
    scene.render();
    fpsFrames++;
    if (now - fpsT0 >= 500) {
      state.fps = (fpsFrames * 1000) / (now - fpsT0);
      fpsFrames = 0;
      fpsT0 = now;
      state.scene = scene.info();
    }
    overlay.update(state, now);
  };
  requestAnimationFrame(frame);
  (window as unknown as { __debug: OverlayState }).__debug = state;
}

boot().catch((e) => {
  pushError(e instanceof Error ? `${e.name}: ${e.message}` : String(e));
  overlay.setVisible(true);
  overlay.update(state, performance.now() + 1000);
});
